import { readPageCache, writePageCache, deletePageCache } from './homeFeedStorage';
import { API_BASE_URL } from '../config/runtime';
import { pageCachePolicy, resourceGroup, changedGroups, WALLET_PATH } from './pageCachePolicy';
import { pageResourceAliases } from './pageCacheAliases';
export { pageCachePolicy } from './pageCachePolicy';
export const PAGE_CACHE_EVENT = 'amposter:page-cache-changed';

const MAX_ENTRIES = 80;
const MAX_ENTRY_BYTES = 150000;
const MAX_SNAPSHOT_BYTES = 2000000;
const OFFLINE_MAX_AGE = 7 * 24 * 60 * 60 * 1000;
const stores = new Map();
let active = null;
const REVISION_KEY = `amposter:page-cache-revision:v1:${API_BASE_URL}`;

function storeFor(accountId) {
  if (!stores.has(accountId)) stores.set(accountId, { memory: new Map(), pending: new Map(), versions: new Map(), invalidatedGroups: new Set(), mutations: new Set(), completedOperations: new Set(), durable: {}, epoch: 0, writes: Promise.resolve(), hydrated: null, disposed: false });
  return stores.get(accountId);
}

export function setPageCacheAccount(accountId, credential) {
  if (active?.accountId !== accountId || active?.credential !== credential) {
    // Old responses cannot populate another account's cache after switching.
    if (active) { const store = storeFor(active.accountId); store.epoch++; store.pending.clear(); }
    active = accountId ? { accountId, credential } : null;
    if (active) { const store = storeFor(accountId); store.epoch++; store.memory.clear(); store.pending.clear(); }
  }
}

export function rotatePageCacheCredential(previous, next) {
  if (active?.credential === previous) active.credential = next;
}

export const pageCacheScope = (credential) => active?.credential === credential ? active : null;
const isActive = (scope) => scope && active === scope;
const clone = (value) => value == null ? value : JSON.parse(JSON.stringify(value));
const groupForKey = key => resourceGroup(key.slice(key.indexOf('/api/')));
const notify = (accountId, groups) => window.dispatchEvent(new CustomEvent(PAGE_CACHE_EVENT, { detail: { accountId, groups } }));

function persist(accountId, store) {
  const entries = Object.entries(store.durable).sort((a, b) => b[1].savedAt - a[1].savedAt);
  let bytes = 0;
  store.durable = Object.fromEntries(entries.filter(([, entry], index) => {
    bytes += JSON.stringify(entry).length;
    return index < MAX_ENTRIES && bytes <= MAX_SNAPSHOT_BYTES;
  }));
  const snapshot = store.durable;
  store.writes = store.writes.then(() => store.disposed ? undefined : writePageCache(accountId, snapshot));
}

async function hydrate(accountId, store) {
  if (!store.hydrated) {
    const epoch = store.epoch;
    const versions = new Map(store.versions);
    store.hydrated = readPageCache(accountId).then((saved) => {
      if (store.disposed || store.epoch !== epoch || saved?.version !== 1 || !saved.entries) return;
      const valid = Object.entries(saved.entries).filter(([key, entry]) => !store.invalidatedGroups.has(groupForKey(key)) && (versions.get(groupForKey(key)) || 0) === (store.versions.get(groupForKey(key)) || 0) && entry && Number.isFinite(entry.savedAt) && Date.now() - entry.savedAt < OFFLINE_MAX_AGE && Object.hasOwn(entry, 'data'));
      store.durable = { ...Object.fromEntries(valid.slice(0, MAX_ENTRIES)), ...store.durable };
    });
  }
  await store.hydrated;
}

export async function readPageResource(scope, key, path, fetcher, { reload = false } = {}) {
  const policy = pageCachePolicy(path);
  if (!policy || !isActive(scope)) return fetcher();
  const store = storeFor(scope.accountId);
  const epoch = store.epoch;
  const group = resourceGroup(path);
  const version = store.versions.get(group) || 0;
  const current = () => !store.disposed && store.epoch === epoch && (store.versions.get(group) || 0) === version && isActive(scope);
  const existing = store.memory.get(key);
  if (!reload && existing && Date.now() - existing.savedAt < policy.maxAge) return clone(existing.data);
  if (store.pending.has(key)) return clone(await store.pending.get(key));
  const request = (async () => {
    // Online launches validate with the server once per resource. Durable
    // snapshots are offline fallback, never proof of login or permissions.
    if (navigator.onLine === false && policy.persist) {
      await hydrate(scope.accountId, store);
      const saved = store.durable[key];
      if (current() && saved && Date.now() - saved.savedAt < OFFLINE_MAX_AGE) return clone(saved.data);
    }
    try {
      if (!isActive(scope)) throw Object.assign(new Error('The active account changed'), { code: 'SESSION_CHANGED' });
      const result = await fetcher();
      // A pre-purchase read must not overwrite the balance in an open view,
      // even if its HTTP response arrives after the purchase response.
      if (!current() && isActive(scope) && !store.disposed) return readPageResource(scope, key, path, fetcher);
      if (current()) {
        const data = clone(result);
        const entry = { data, savedAt: Date.now() };
        const identity = key.slice(0, key.indexOf('/api/'));
        const keys = [key, ...pageResourceAliases(path, data).map(alias => `${identity}${alias}`)];
        const previousEntries = new Set(keys.map(alias => store.memory.get(alias)).filter(Boolean));
        // A fresh UUID read after a handle change also retires the old handle.
        for (const [cachedKey, cachedEntry] of store.memory) {
          if (previousEntries.has(cachedEntry)) store.memory.delete(cachedKey);
        }
        for (const alias of keys) store.memory.set(alias, entry);
        while (store.memory.size > MAX_ENTRIES) {
          const oldest = [...store.memory.keys()].find(cachedKey => groupForKey(cachedKey) !== 'wallet') || store.memory.keys().next().value;
          store.memory.delete(oldest);
        }
        if (policy.persist && JSON.stringify(data)?.length <= MAX_ENTRY_BYTES) {
          await hydrate(scope.accountId, store);
          if (current()) { store.durable = { ...store.durable, [key]: entry }; persist(scope.accountId, store); }
        }
      }
      if (!isActive(scope)) throw Object.assign(new Error('The active account changed'), { code: 'SESSION_CHANGED' });
      if (!current() && !store.disposed) return readPageResource(scope, key, path, fetcher);
      return result;
    } catch (error) {
      if (policy.persist && ['NETWORK_ERROR', 'REQUEST_TIMEOUT'].includes(error.code)) {
        await hydrate(scope.accountId, store);
        const saved = store.durable[key];
        if (current() && saved && Date.now() - saved.savedAt < OFFLINE_MAX_AGE) return clone(saved.data);
      }
      if (current() && [401, 403, 404].includes(error.status)) {
        const deniedEntry = store.memory.get(key);
        if (deniedEntry) for (const [cachedKey, entry] of store.memory) {
          if (entry === deniedEntry) store.memory.delete(cachedKey);
        }
        store.memory.delete(key);
        if (store.durable[key]) { delete store.durable[key]; persist(scope.accountId, store); }
      }
      throw error;
    }
  })();
  store.pending.set(key, request);
  try { return clone(await request); }
  finally { if (store.pending.get(key) === request) store.pending.delete(key); }
}

export function invalidatePageCache(accountId = active?.accountId, broadcast = true, groups = null) {
  if (!accountId) return;
  const store = storeFor(accountId);
  if (groups) {
    groups.forEach(group => { store.invalidatedGroups.add(group); store.versions.set(group, (store.versions.get(group) || 0) + 1); });
    for (const map of [store.memory, store.pending]) for (const key of map.keys()) if (groups.includes(groupForKey(key))) map.delete(key);
    store.durable = Object.fromEntries(Object.entries(store.durable).filter(([key]) => !groups.includes(groupForKey(key))));
  } else {
    store.epoch++;
    store.memory.clear();
    store.pending.clear();
    store.durable = {};
    // Do not reload a snapshot invalidated by a mutation.
    store.hydrated = Promise.resolve();
  }
  for (const mutation of store.mutations) if (!groups || groups.includes('wallet')) mutation.conflicted = true;
  // Hydrate unaffected durable entries before writing a partial snapshot.
  if (groups) {
    if (groups.some(group => ['store', 'progress', 'talent-catalog', 'other'].includes(group))) {
      void hydrate(accountId, store).then(() => { if (!store.disposed) persist(accountId, store); });
    }
  } else persist(accountId, store);
  if (broadcast) {
    try { localStorage.setItem(REVISION_KEY, JSON.stringify({ accountId, groups, revision: `${Date.now()}:${Math.random()}` })); } catch (_) { /* optional cross-tab sync */ }
  }
  notify(accountId, groups);
}

export function beginPageCacheWrite(path, scope, body) {
  if (!isActive(scope)) return null;
  const groups = changedGroups(path, body);
  const store = storeFor(scope.accountId);
  const ticket = { scope, groups, store, conflicted: false, epoch: store.epoch, versions: new Map(store.versions), operationKey: body?.idempotencyKey };
  if (!groups || groups.includes('wallet')) {
    ticket.conflicted = store.mutations.size > 0;
    for (const other of store.mutations) other.conflicted = true;
    store.mutations.add(ticket);
  }
  return ticket;
}

export function finishPageCacheWrite(ticket, path, payload, { failed = false } = {}) {
  if (!ticket) return;
  const { scope, store, groups } = ticket;
  store.mutations.delete(ticket);
  if (!isActive(scope) || store.disposed || (groups && !groups.length)) return;
  const canSeed = !failed && !ticket.conflicted && store.epoch === ticket.epoch
    && (groups || []).every(group => (store.versions.get(group) || 0) === (ticket.versions.get(group) || 0));
  if (ticket.operationKey) {
    store.completedOperations.add(ticket.operationKey);
    if (store.completedOperations.size > MAX_ENTRIES) store.completedOperations.delete(store.completedOperations.values().next().value);
  }
  invalidatePageCache(scope.accountId, true, groups);
  // Only authoritative responses can seed money/selection data. Concurrent or
  // interrupted writes fall back to one shared read; never subtract locally.
  if (canSeed && path === '/api/v1/store/purchases' && payload?.wallet?.balances && Array.isArray(payload.wallet.currencies)) {
    store.memory.set(`default:${WALLET_PATH}`, { data: clone(payload.wallet), savedAt: Date.now() });
  }
  if (canSeed && path.startsWith('/api/v1/me/cosmetics/active-') && payload) {
    store.memory.set('default:/api/v1/me/cosmetics/selection', { data: clone(payload), savedAt: Date.now() });
  }
}

export function cancelPageCacheWrite(ticket) {
  if (ticket) ticket.store.mutations.delete(ticket);
}

export async function clearPageCache(accountId) {
  if (active?.accountId === accountId) active = null;
  const store = stores.get(accountId);
  stores.delete(accountId);
  if (store) { store.disposed = true; store.pending.clear(); await store.writes; }
  await deletePageCache(accountId);
}

// Reconnect and cross-tab edits invalidate memory; each page reads fresh data
// on its next request. No eager request burst for pages the user never visits.
export function installPageCacheLifecycle() {
  const reconnect = () => {
    if (!active) return;
    const store = storeFor(active.accountId);
    store.epoch++; store.memory.clear(); store.pending.clear();
    for (const mutation of store.mutations) mutation.conflicted = true;
    notify(active.accountId, null);
  };
  const changed = () => invalidatePageCache(active?.accountId, true, ['posts', 'profiles', 'progress']);
  const blocked = () => invalidatePageCache();
  const realtime = event => {
    const { type, data } = event.detail || {};
    if (type === 'resync' || type === 'reconnected' || type === 'access.changed') reconnect();
    if (type === 'wallet.changed' || (type === 'notification.changed' && data?.read !== true && data?.relatedEntityType !== 'WALLET_PAYMENT_REQUEST' && (data?.type === 'WALLET' || data?.payload?.giftAmount || data?.payload?.giftCurrency))) {
      if (!active) return;
      const store = storeFor(active.accountId);
      if (data?.operationKey && (store.completedOperations.has(data.operationKey) || [...store.mutations].some(ticket => ticket.operationKey === data.operationKey))) return;
      const groups = ['wallet', ...(Array.isArray(data?.groups) ? data.groups.filter(group => ['inventory', 'cosmetics'].includes(group)) : [])];
      invalidatePageCache(active.accountId, false, groups);
    }
  };
  const storage = (event) => {
    if (event.key === 'amposter_backend_session' && active && event.newValue !== active.credential) {
      const accountId = active.accountId;
      setPageCacheAccount(null, null);
      if (!event.newValue) void clearPageCache(accountId);
      return;
    }
    if (event.key !== REVISION_KEY || !event.newValue) return;
    try { const value = JSON.parse(event.newValue); if (value.accountId === active?.accountId) invalidatePageCache(active.accountId, false, Array.isArray(value.groups) ? value.groups : null); } catch (_) { /* ignore damaged metadata */ }
  };
  window.addEventListener('online', reconnect);
  window.addEventListener('storage', storage);
  window.addEventListener('amposter:cache-realtime', realtime);
  window.addEventListener('amposter:user-blocked', blocked);
  const events = ['postCreated', 'postUpdated', 'postDeleted'];
  events.forEach((name) => window.addEventListener(name, changed));
  return () => { window.removeEventListener('online', reconnect); window.removeEventListener('storage', storage); window.removeEventListener('amposter:cache-realtime', realtime); window.removeEventListener('amposter:user-blocked', blocked); events.forEach((name) => window.removeEventListener(name, changed)); };
}
